/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { getRegistrantDisplayName } from '@sportninja/common/utils/billing'
import { BUCKET_IMAGE_SRC } from 'src/components/ts-components/icons/bucketImage'
import Text from 'src/components/ts-components/Text'
import {
  getRegistrationDetailsPath,
  isRegistrationNameClickable,
  isRegistrationSession,
} from '../../utils/registrationRows'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'
const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

export interface NameCellParams {
  onResumeSession: (record: UserRegistration) => void
}

type NameCellProps = ICellRendererParams<UserRegistration> & NameCellParams

const fallBackTo =
  (src: string) =>
  ({ currentTarget }: { currentTarget: HTMLImageElement }) => {
    currentTarget.onerror = null
    currentTarget.src = src
  }

/** The row's logo and name; opens the session, or the registration's details. */
const NameCell = ({ data: record, onResumeSession }: NameCellProps) => {
  if (!record) {
    return null
  }

  const handleClick = () => {
    if (isRegistrationSession(record)) {
      onResumeSession(record)
    } else if (record.billing?.length > 0) {
      window.location.href = getRegistrationDetailsPath(record)
    }
  }

  const teamName = record.team?.name

  if (!teamName) {
    const isTeamRegistration = record.registration_type === 'Team'

    return (
      <Tooltip title='Restore Session'>
        <div css={[rowStyle, clickableStyle]} onClick={handleClick}>
          <img
            src={isTeamRegistration ? TEAM_PLACEHOLDER : PLAYER_PLACEHOLDER}
            alt='team logo'
            css={logoStyle}
            onError={fallBackTo(TEAM_PLACEHOLDER)}
          />
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {isTeamRegistration
              ? 'New Team Registration'
              : 'New Player Registration'}
          </Text>
        </div>
      </Tooltip>
    )
  }

  const isBucket = record.team?.is_bucket
  // SN-8480 — a Team Rep's own budget line is named for its payer, over the team.
  const playerName = getRegistrantDisplayName(record)
  const placeholder = playerName ? PLAYER_PLACEHOLDER : TEAM_PLACEHOLDER
  const image = playerName
    ? getImageThumbnailId(record.player)
    : getImageThumbnailId(record.team)

  return (
    <div
      css={[rowStyle, isRegistrationNameClickable(record) && clickableStyle]}
      onClick={handleClick}
    >
      <img
        src={isBucket ? BUCKET_IMAGE_SRC : image || placeholder}
        alt='team logo'
        css={[logoStyle, isBucket && bucketLogoStyle]}
        onError={fallBackTo(isBucket ? BUCKET_IMAGE_SRC : placeholder)}
      />
      <div css={namesStyle}>
        {playerName ? (
          <>
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.WHITE}
              style={ELLIPSIS}
            >
              {playerName}
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
              style={ELLIPSIS}
            >
              {teamName}
            </Text>
          </>
        ) : (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={ELLIPSIS}
          >
            {teamName}
          </Text>
        )}
      </div>
    </div>
  )
}

export default NameCell

// ─── Styles ──────────────────────────────────────────────────────

const ELLIPSIS = `
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`

const clickableStyle = css`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const logoStyle = css`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: ${colors.WHITE};
  border-radius: 4px;
  padding: 2px;
  object-fit: contain;
`

const bucketLogoStyle = css`
  border-radius: 8px;
  padding: 0;
`

const namesStyle = css`
  min-width: 0;
  line-height: 20px;
`
